import React, { useState, useEffect } from 'react';
import { X, Gift, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const LeadCapturePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    // Check if popup was already shown in this session
    const popupShown = sessionStorage.getItem('leadPopupShown');
    
    if (!popupShown) {
      // Show popup after 30 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
        sessionStorage.setItem('leadPopupShown', 'true');
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Send to backend API as a consultation request
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consultations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          service: 'popup-lead-capture',
          message: 'Lead captured via 30-second popup offer'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsSubmitted(true);
      toast.success('🎉 Success! We\'ll contact you within 24 hours!');

      // Close popup after 3 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Failed to submit. Please try again or call us directly.');
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
        onClick={handleClose}
      ></div>

      {/* Popup */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <Card 
          className="pointer-events-auto border-0 shadow-2xl max-w-lg w-full animate-scale-in relative max-h-[90vh] flex flex-col my-auto"
          style={{
            animation: 'slideUp 0.5s ease-out'
          }}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
          >
            <X size={18} className="text-gray-600" />
          </button>

          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1565C0] via-[#0D47A1] to-[#1565C0] opacity-5"></div>

          <CardContent className="p-0 relative overflow-y-auto">
            {isSubmitted ? (
              // Success State
              <div className="p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#8BC34A] to-[#7CB342] flex items-center justify-center mx-auto mb-6 animate-bounce">
                  <Sparkles size={40} className="text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">You're In! 🎉</h3>
                <p className="text-lg text-gray-600 mb-2">
                  Our expert counselor will contact you within 24 hours.
                </p>
                <p className="text-sm text-gray-500">
                  Check your email for confirmation!
                </p>
              </div>
            ) : (
              // Form State
              <>
                {/* Header Section */}
<div className="bg-gradient-to-br from-[#1565C0] to-[#0D47A1] pt-12 pb-6 px-6 sm:px-8 text-center relative overflow-hidden">
                  {/* Decorative Elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                  <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#FF7043]/20 rounded-full blur-3xl"></div>
                  
                  <div className="relative z-10">
                    <div className="inline-flex items-center gap-2 bg-[#FF7043] text-white px-4 py-2 rounded-full text-xs sm:text-sm font-bold mb-4 shadow-lg">
                      <Gift size={16} />
                      <span>LIMITED TIME OFFER</span>
                    </div>
                    
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
                      FREE Consultation Worth ₹5,000!
                    </h2>
                    
                    <p className="text-white/90 text-base sm:text-lg mb-2">
                      Expert Guidance - Absolutely FREE
                    </p>
                    
                    <div className="flex items-center justify-center gap-4 sm:gap-6 mt-6">
                      <div className="text-center">
                        <div className="text-xl sm:text-2xl font-bold text-white">95%</div>
                        <div className="text-xs text-white/80">Visa Success</div>
                      </div>
                      <div className="h-8 w-px bg-white/30"></div>
                      <div className="text-center">
                        <div className="text-xl sm:text-2xl font-bold text-white">10K+</div>
                        <div className="text-xs text-white/80">Students Placed</div>
                      </div>
                      <div className="h-8 w-px bg-white/30"></div>
                      <div className="text-center">
                        <div className="text-xl sm:text-2xl font-bold text-white">25+</div>
                        <div className="text-xs text-white/80">Countries</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Section */}
                <div className="p-6 sm:p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                      🎯 Book Your FREE Consultation Now
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Our experts will guide you through your study abroad journey
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your Full Name *"
                        className="w-full py-6 text-base"
                        required
                      />
                    </div>

                    <div>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email Address *"
                        className="w-full py-6 text-base"
                        required
                      />
                    </div>

                    <div>
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="WhatsApp Number *"
                        className="w-full py-6 text-base"
                        required
                      />
                    </div>

                    <div>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-[#1565C0] focus:border-transparent"
                      >
                        <option value="">Interested Country (Optional)</option>
                        <option value="canada">🇨🇦 Canada</option>
                        <option value="usa">🇺🇸 USA</option>
                        <option value="uk">🇬🇧 United Kingdom</option>
                        <option value="australia">🇦🇺 Australia</option>
                        <option value="europe">🇪🇺 Europe</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#FF7043] to-[#F4511E] hover:from-[#F4511E] hover:to-[#FF7043] text-white py-6 text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 group"
                    >
                      Claim My FREE Consultation
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                    </Button>

                    <p className="text-xs text-gray-500 text-center mt-4">
                      🔒 Your information is 100% secure. We'll contact you within 24 hours.
                    </p>
                  </form>

                  {/* Trust Badges */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-center gap-4 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="text-[#8BC34A]">✓</span> No Hidden Charges
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[#8BC34A]">✓</span> Expert Guidance
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[#8BC34A]">✓</span> 24hr Response
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default LeadCapturePopup;
